import React from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';

import LoadingBar from '../../LoadingBar';
import NoAppointments from './NoAppointments';
import ProviderImage from '../../ProviderImage';
import Appointments from './Appointments';
import { constructAddress } from '../../../utils/practice';
import { PracticeOnly, ProviderAndPractice, SchedulingConfig } from './common';
import { getProviderNameFull } from '../../../utils/provider';
import { ModalContainer } from '../../ModalContainer';
import { ReactComponent as MapPinSvg } from '../../../assets/map-pin.svg';

import * as api from '../../../api';

import styles from '../index.module.css';

interface Props extends Modal.Props {
  onAppointmentSelected: (appointment: api.EhrTimeSlot.EhrTimeSlot) => void;
  onModalClose: (value: boolean) => void;
  providerEhrTimeSlots: api.EhrTimeSlot.EhrTimeSlot[] | null;
  config: ProviderAndPractice | PracticeOnly;
  fieldConfig: SchedulingConfig;
}

const AppointmentModal: React.FC<Props> = ({
  onAppointmentSelected,
  onModalClose,
  providerEhrTimeSlots,
  config,
  fieldConfig,
  ...props
}) => {
  const { t } = useTranslation();
  const practiceAddress = constructAddress(config);
  return (
    <ModalContainer
      contentClassName={styles.AppointmentModalBody}
      childrenContainerClassName={styles.AppointmentModalProviderContainer}
      isOpen={props.isOpen}
      onModalClose={() => onModalClose(false)}
      topLabel={t('All appointments')}
      noPadding={true}
      component={
        <>
          {!providerEhrTimeSlots ? (
            <div className={styles.AppointmentModalLoadingBar}>
              <LoadingBar label={t('Gathering provider availability')} />
            </div>
          ) : (
            <>
              <div className={styles.AppointmentModalProviderInfo}>
                <ProviderImage
                  provider={
                    'providerUserId' in config
                      ? config
                      : {
                          firstName: config.name,
                          lastName: '',
                          previsitImageUrl: '',
                        }
                  }
                  className={styles.AppointmentModalProviderImage}
                  size="Small"
                />
                <div className={styles.AppointmentModalProviderInfoText}>
                  <span>
                    {'providerUserId' in config
                      ? getProviderNameFull({
                          provider: config,
                        })
                      : config.name}
                  </span>
                  {practiceAddress && (
                    <div className={styles.ProviderAdditional}>
                      <MapPinSvg />
                      {practiceAddress}
                    </div>
                  )}
                </div>
              </div>
              <hr className={styles.AppointmentModalDivider} />
              {providerEhrTimeSlots.length === 0 ? (
                <NoAppointments
                  className={styles.MultiProviderNoAppointmentContent}
                  fieldConfig={fieldConfig}
                />
              ) : (
                <div className={styles.MultiProviderAppointmentContainer}>
                  <Appointments
                    timeSlots={providerEhrTimeSlots}
                    providerId={
                      'providerUserId' in config
                        ? config.providerUserId
                        : undefined
                    }
                    practiceId={config.practiceId}
                    timezone={config.timezone}
                    onAppointmentSelected={onAppointmentSelected}
                    modal={true}
                  />
                </div>
              )}
            </>
          )}
        </>
      }
    />
  );
};

export default AppointmentModal;
